/**
 * Geocoding API Caching System for MJ Bale
 * 
 * Reduces unnecessary Google Geocoding API calls by caching postcode → suburb/state 
 * lookups for the duration of a user session. Includes optional cookie-based cache 
 * to share lookups across tabs in the same browser session.
 * 
 * Features:
 * - Session storage cache (per-tab, full geocoding results)
 * - Cookie cache (cross-tab, tiny records only)
 * - Query normalization for consistent cache keys
 * - LRU eviction for cookie size management
 * - Safe error handling for storage operations
 * - Force refresh capability
 */

// Constants
const GEOCODING_CACHE_KEY = 'mjbale_geocoding_cache_v1';  // sessionStorage (full)
const GEOCODING_COOKIE_KEY = 'mjbale_geocoding_ck_v1';    // cookie (tiny)
const GEOCODING_COOKIE_MAX_ENTRIES = 20;
const GEOCODING_STATS_KEY = 'mjbale_geocoding_stats_v1';  // sessionStorage (stats)

/**
 * Normalized geocoding result type
 * @typedef {Object} NormalizedGeocode
 * @property {string} formattedAddress - Full formatted address
 * @property {Object|null} location - Coordinates object with lat/lng or null
 * @property {number} location.lat - Latitude
 * @property {number} location.lng - Longitude
 * @property {Object} components - Address components
 * @property {string} components.postal_code - Postal/ZIP code
 * @property {string} components.locality - City/suburb (locality | postal_town | sublocality)
 * @property {string} components.administrative_area_level_1 - State/province
 * @property {string} components.country - Country code
 */

/**
 * Tiny cache record for cookies
 * @typedef {Object} TinyRecord
 * @property {number} lat - Latitude
 * @property {number} lng - Longitude
 * @property {string} pc - Postal code
 * @property {string} suburb - Locality/suburb
 * @property {string} state - State/province
 * @property {string} addr - Formatted address (truncated)
 * @property {number} ts - Timestamp for LRU
 */

// =============================================================================
// NORMALIZATION HELPERS
// =============================================================================

/**
 * Normalize a query string for consistent cache keying
 * @param {string} query - Raw query string
 * @returns {string} Normalized cache key
 */
function normalizeQuery(query) {
  if (typeof query !== 'string') return '';
  
  return query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .replace(/[^\w\s,.-]/g, ''); // Remove special chars except common ones
}

/**
 * Create a cache key for reverse geocoding (lat,lng lookup)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} precision - Decimal places for rounding (default: 4)
 * @returns {string} Normalized reverse geocode key
 */
function createReverseGeocodeKey(lat, lng, precision = 4) {
  const roundedLat = Number(lat).toFixed(precision);
  const roundedLng = Number(lng).toFixed(precision);
  return `reverse:${roundedLat},${roundedLng}`;
}

// =============================================================================
// MONITORING AND STATISTICS
// =============================================================================

/**
 * API usage statistics tracking
 */
let sessionStats = {
  geocoding: {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    sessionCacheHits: 0,
    cookieCacheHits: 0,
    apiCalls: 0,
    errors: 0,
    costSavings: 0, // Estimated cost savings
    lastReset: Date.now()
  },
  autocomplete: {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    apiCalls: 0,
    errors: 0,
    costSavings: 0,
    lastReset: Date.now()
  },
  places: {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    apiCalls: 0,
    errors: 0,
    costSavings: 0,
    lastReset: Date.now()
  }
};

/**
 * Load stats from sessionStorage
 */
function loadStats() {
  try {
    const stored = sessionStorage.getItem(GEOCODING_STATS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      sessionStats = { ...sessionStats, ...parsed };
    }
  } catch (error) {
    console.warn('[GeocodingCache] Failed to load stats:', error);
  }
}

/**
 * Save stats to sessionStorage
 */
function saveStats() {
  try {
    sessionStorage.setItem(GEOCODING_STATS_KEY, JSON.stringify(sessionStats));
  } catch (error) {
    console.warn('[GeocodingCache] Failed to save stats:', error);
  }
}

/**
 * Track a geocoding request
 * @param {string} type - Type of operation ('hit', 'miss', 'api_call', 'error')
 * @param {string} source - Source of cache hit ('session', 'cookie', or null for miss)
 * @param {string} category - Category ('geocoding', 'autocomplete', 'places')
 */
function trackStats(type, source = null, category = 'geocoding') {
  if (!sessionStats[category]) return;
  
  sessionStats[category].totalRequests++;
  
  switch (type) {
    case 'hit':
      sessionStats[category].cacheHits++;
      if (source === 'session') sessionStats[category].sessionCacheHits++;
      if (source === 'cookie') sessionStats[category].cookieCacheHits++;
      // Estimate cost savings (approximate Google API pricing)
      sessionStats[category].costSavings += category === 'geocoding' ? 0.005 : 0.003;
      break;
    case 'miss':
      sessionStats[category].cacheMisses++;
      break;
    case 'api_call':
      sessionStats[category].apiCalls++;
      break;
    case 'error':
      sessionStats[category].errors++;
      break;
  }
  
  saveStats();
}

/**
 * Get comprehensive statistics
 */
function getStats() {
  loadStats();
  
  const stats = { ...sessionStats };
  
  // Calculate additional metrics
  Object.keys(stats).forEach(category => {
    if (stats[category].totalRequests > 0) {
      stats[category].hitRate = (stats[category].cacheHits / stats[category].totalRequests * 100).toFixed(2);
      stats[category].missRate = (stats[category].cacheMisses / stats[category].totalRequests * 100).toFixed(2);
      stats[category].errorRate = (stats[category].errors / stats[category].totalRequests * 100).toFixed(2);
    } else {
      stats[category].hitRate = '0.00';
      stats[category].missRate = '0.00';
      stats[category].errorRate = '0.00';
    }
  });
  
  return stats;
}

/**
 * Reset statistics
 */
function resetStats() {
  sessionStats = {
    geocoding: {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      sessionCacheHits: 0,
      cookieCacheHits: 0,
      apiCalls: 0,
      errors: 0,
      costSavings: 0,
      lastReset: Date.now()
    },
    autocomplete: {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiCalls: 0,
      errors: 0,
      costSavings: 0,
      lastReset: Date.now()
    },
    places: {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiCalls: 0,
      errors: 0,
      costSavings: 0,
      lastReset: Date.now()
    }
  };
  saveStats();
}

/**
 * Generate a detailed monitoring report
 */
function generateReport() {
  const stats = getStats();
  const totalSavings = Object.keys(stats).reduce((sum, cat) => sum + (stats[cat].costSavings || 0), 0);
  const totalRequests = Object.keys(stats).reduce((sum, cat) => sum + (stats[cat].totalRequests || 0), 0);
  const totalCacheHits = Object.keys(stats).reduce((sum, cat) => sum + (stats[cat].cacheHits || 0), 0);
  const overallHitRate = totalRequests > 0 ? (totalCacheHits / totalRequests * 100).toFixed(2) : '0.00';
  
  return {
    summary: {
      totalRequests,
      totalCacheHits,
      overallHitRate: `${overallHitRate}%`,
      estimatedCostSavings: `$${totalSavings.toFixed(4)}`,
      sessionDuration: Math.round((Date.now() - Math.min(...Object.keys(stats).map(cat => stats[cat].lastReset))) / 1000 / 60)
    },
    breakdown: stats,
    recommendations: generateRecommendations(stats)
  };
}

/**
 * Generate optimization recommendations based on stats
 */
function generateRecommendations(stats) {
  const recommendations = [];
  
  Object.keys(stats).forEach(category => {
    const categoryStats = stats[category];
    const hitRate = parseFloat(categoryStats.hitRate);
    
    if (hitRate < 30) {
      recommendations.push(`${category}: Low hit rate (${hitRate}%) - consider increasing cache duration or size`);
    } else if (hitRate > 80) {
      recommendations.push(`${category}: Excellent hit rate (${hitRate}%) - cache is working well`);
    }
    
    if (categoryStats.errors > categoryStats.totalRequests * 0.1) {
      recommendations.push(`${category}: High error rate - check API configuration`);
    }
  });
  
  return recommendations;
}

// Initialize stats on load
loadStats();

// =============================================================================
// SESSION STORAGE HELPERS (FULL CACHE)
// =============================================================================

/**
 * Get the full geocoding cache from sessionStorage
 * @returns {Object} Cache object or empty object if not found/invalid
 */
function getGeocodingCache() {
  try {
    const cached = sessionStorage.getItem(GEOCODING_CACHE_KEY);
    if (!cached) return {};
    
    const parsed = JSON.parse(cached);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (error) {
    console.warn('[GeocodingCache] Failed to read session cache:', error);
    return {};
  }
}

/**
 * Save a geocoding result to sessionStorage
 * @param {string} key - Normalized cache key
 * @param {NormalizedGeocode} value - Full geocoding result
 */
function saveToGeocodingCache(key, value) {
  try {
    const cache = getGeocodingCache();
    cache[key] = {
      ...value,
      timestamp: Date.now(),
      fromCache: false
    };
    
    sessionStorage.setItem(GEOCODING_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[GeocodingCache] Failed to save to session cache:', error);
  }
}

// =============================================================================
// COOKIE HELPERS (TINY CACHE)
// =============================================================================

/**
 * Convert a full geocoding result to a tiny record for cookie storage
 * @param {NormalizedGeocode} geocodeResult - Full geocoding result
 * @returns {TinyRecord|null} Tiny record or null if invalid
 */
function toTinyRecord(geocodeResult) {
  try {
    if (!geocodeResult?.location?.lat || !geocodeResult?.location?.lng) {
      return null;
    }
    
    return {
      lat: Number(geocodeResult.location.lat),
      lng: Number(geocodeResult.location.lng),
      pc: geocodeResult.components?.postal_code || '',
      suburb: geocodeResult.components?.locality || '',
      state: geocodeResult.components?.administrative_area_level_1 || '',
      addr: (geocodeResult.formattedAddress || '').substring(0, 100), // Truncate to save cookie space
      ts: Date.now()
    };
  } catch (error) {
    console.warn('[GeocodingCache] Failed to create tiny record:', error);
    return null;
  }
}

/**
 * Convert a tiny record back to a partial NormalizedGeocode
 * @param {TinyRecord} tinyRecord - Tiny cache record
 * @returns {NormalizedGeocode} Partial geocoding result
 */
function fromTinyRecord(tinyRecord) {
  // Use the stored formatted address if available, otherwise reconstruct from components
  let formattedAddress = tinyRecord.addr;
  
  if (!formattedAddress) {
    // Fallback: reconstruct from components (for backwards compatibility)
    const parts = [];
    if (tinyRecord.suburb) parts.push(tinyRecord.suburb);
    if (tinyRecord.state) parts.push(tinyRecord.state);
    if (tinyRecord.pc) parts.push(tinyRecord.pc);
    formattedAddress = parts.join(', ') || 'Unknown Location';
  }
  
  return {
    formattedAddress: formattedAddress,
    location: {
      lat: tinyRecord.lat,
      lng: tinyRecord.lng
    },
    components: {
      postal_code: tinyRecord.pc,
      locality: tinyRecord.suburb,
      administrative_area_level_1: tinyRecord.state,
      country: 'AU' // Assuming Australia for tiny records
    },
    fromCache: true,
    fromCookie: true
  };
}

/**
 * Read the session cookie cache
 * @returns {Object} Cookie cache object or empty object if not found/invalid
 */
function readSessionCookie() {
  try {
    const cookies = document.cookie.split(';');
    const targetCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${GEOCODING_COOKIE_KEY}=`)
    );
    
    if (!targetCookie) return {};
    
    const cookieValue = targetCookie.split('=')[1];
    if (!cookieValue) return {};
    
    const decoded = decodeURIComponent(cookieValue);
    const parsed = JSON.parse(decoded);
    
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (error) {
    console.warn('[GeocodingCache] Failed to read session cookie:', error);
    return {};
  }
}

/**
 * Write to the session cookie cache with LRU eviction
 * @param {string} key - Normalized cache key
 * @param {TinyRecord} tinyRecord - Tiny record to store
 */
function writeSessionCookie(key, tinyRecord) {
  try {
    let cookieCache = readSessionCookie();
    
    // Add/update the entry
    cookieCache[key] = tinyRecord;
    
    // Implement simple LRU eviction if we exceed max entries
    const entries = Object.entries(cookieCache);
    if (entries.length > GEOCODING_COOKIE_MAX_ENTRIES) {
      // Sort by timestamp (oldest first) and remove oldest entries
      entries.sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));
      
      const entriesToKeep = entries.slice(-GEOCODING_COOKIE_MAX_ENTRIES);
      cookieCache = Object.fromEntries(entriesToKeep);
      
      // Make sure our new entry is still there
      cookieCache[key] = tinyRecord;
    }
    
    const cookieValue = encodeURIComponent(JSON.stringify(cookieCache));
    
    // Set cookie for session only (no expiry = session cookie)
    // SameSite=Lax for reasonable security while allowing cross-tab access
    document.cookie = `${GEOCODING_COOKIE_KEY}=${cookieValue}; path=/; SameSite=Lax`;
  } catch (error) {
    console.warn('[GeocodingCache] Failed to write session cookie:', error);
  }
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Look up a geocoding result from cache (session first, then cookie)
 * @param {string} query - Raw query string
 * @returns {NormalizedGeocode|null} Cached result or null if not found
 */
function lookupGeocodeCache(query) {
  const normalizedKey = normalizeQuery(query);
  if (!normalizedKey) return null;
  
  // First check session storage (full cache)
  const sessionCache = getGeocodingCache();
  if (sessionCache[normalizedKey]) {
    trackStats('hit', 'session', 'geocoding');
    return {
      ...sessionCache[normalizedKey],
      fromCache: true,
      fromCookie: false
    };
  }
  
  // Then check cookie cache (tiny records)
  const cookieCache = readSessionCookie();
  if (cookieCache[normalizedKey]) {
    const result = fromTinyRecord(cookieCache[normalizedKey]);
    
    // Also save to session storage for faster future access
    saveToGeocodingCache(normalizedKey, result);
    
    trackStats('hit', 'cookie', 'geocoding');
    return result;
  }
  
  trackStats('miss', null, 'geocoding');
  return null;
}

/**
 * Store a geocoding result in both session storage and cookie cache
 * @param {string} query - Raw query string
 * @param {NormalizedGeocode} fullResult - Complete geocoding result
 */
function storeGeocodeResult(query, fullResult) {
  const normalizedKey = normalizeQuery(query);
  if (!normalizedKey) return;
  
  // Save full result to session storage
  saveToGeocodingCache(normalizedKey, fullResult);
  
  // Save tiny record to cookie cache
  const tinyRecord = toTinyRecord(fullResult);
  if (tinyRecord) {
    writeSessionCookie(normalizedKey, tinyRecord);
  }
}

/**
 * Main geocoding function with caching
 * @param {string} query - Location query string
 * @param {Function} geocodeFn - Function that performs actual geocoding
 * @param {Object} options - Options object
 * @param {boolean} options.forceRefresh - Bypass cache and force fresh lookup
 * @returns {Promise<NormalizedGeocode>} Geocoding result with cache info
 */
async function geocodeWithCache(query, geocodeFn, options = {}) {
  const { forceRefresh = false } = options;
  
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = lookupGeocodeCache(query);
    if (cached) {
      return cached;
    }
  }
  
  // Call the actual geocoding function
  try {
    trackStats('api_call', null, 'geocoding');
    const result = await geocodeFn(query);
    
    // Store in cache
    storeGeocodeResult(query, result);
    
    return {
      ...result,
      fromCache: false,
      fromCookie: false
    };
  } catch (error) {
    trackStats('error', null, 'geocoding');
    console.error('[GeocodingCache] Geocoding failed:', error);
    throw error;
  }
}

// =============================================================================
// EXAMPLE GEOCODING FUNCTION
// =============================================================================

/**
 * Alternative geocoding using Google Places API (works with referrer restrictions)
 * This uses the same API that's already working in your StoreFinder component
 * @param {string} query - Location query
 * @returns {Promise<NormalizedGeocode>} Normalized geocoding result
 */
async function googleGeocodeWithPlaces(query) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.places) {
      reject(new Error('Google Places API not loaded. Make sure Google Maps is loaded with places library.'));
      return;
    }

    console.log(`[GeocodingCache] Using Places API for: "${query}"`);

    // Use Places Autocomplete to find the location
    const service = new window.google.maps.places.AutocompleteService();
    
    service.getPlacePredictions({
      input: query,
      types: ['geocode'],
      componentRestrictions: { country: 'AU' }
    }, (predictions, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions || predictions.length === 0) {
        reject(new Error(`No results found for: ${query}. Status: ${status}`));
        return;
      }

      // Get details for the first prediction
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      placesService.getDetails({
        placeId: predictions[0].place_id,
        fields: ['geometry', 'formatted_address', 'address_components', 'name']
      }, (place, detailStatus) => {
        if (detailStatus !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          reject(new Error(`Failed to get place details. Status: ${detailStatus}`));
          return;
        }

        // Extract components
        const components = {};
        if (place.address_components) {
          place.address_components.forEach(component => {
            component.types.forEach(type => {
              components[type] = component.long_name;
            });
          });
        }

        // Prefer locality, fall back to postal_town or sublocality_level_1
        const locality = components.locality || 
                        components.postal_town || 
                        components.sublocality_level_1 || 
                        '';

        const result = {
          formattedAddress: place.formatted_address || place.name,
          location: place.geometry?.location ? {
            lat: typeof place.geometry.location.lat === 'function' 
              ? place.geometry.location.lat() 
              : place.geometry.location.lat,
            lng: typeof place.geometry.location.lng === 'function' 
              ? place.geometry.location.lng() 
              : place.geometry.location.lng
          } : null,
          components: {
            postal_code: components.postal_code || '',
            locality: locality,
            administrative_area_level_1: components.administrative_area_level_1 || '',
            country: components.country || 'AU'
          }
        };

        console.log(`[GeocodingCache] Places API success:`, result);
        resolve(result);
      });
    });
  });
}

/**
 * Example Google Geocoding API implementation for Australia
 * @param {string} query - Location query
 * @param {string} apiKey - Optional API key, will try to get from Shopify settings if not provided
 * @returns {Promise<NormalizedGeocode>} Normalized geocoding result
 */
async function googleGeocodeAU(query, apiKey = null) {
  // First try the Places API since it works with referrer restrictions
  try {
    return await googleGeocodeWithPlaces(query);
  } catch (placesError) {
    console.warn('[GeocodingCache] Places API failed, trying Geocoding API:', placesError.message);
  }

  // Fallback to Geocoding API (will likely fail with referrer restrictions)
  // Try to get API key from multiple sources
  let API_KEY = apiKey;
  
  // If no API key provided, try to get from Shopify theme settings
  if (!API_KEY && typeof window !== 'undefined' && window.theme?.settings?.google_maps_api_key) {
    API_KEY = window.theme.settings.google_maps_api_key;
  }
  
  // Fallback to environment variable for non-Shopify environments
  if (!API_KEY && typeof process !== 'undefined' && process.env?.REACT_APP_GOOGLE_GEOCODING_API_KEY) {
    API_KEY = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY;
  }
  
  if (!API_KEY) {
    throw new Error('Google Geocoding API key not configured. Please set it in theme settings or environment variables.');
  }

  const params = new URLSearchParams({
    address: query,
    components: 'country:AU', // Restrict to Australia
    key: API_KEY
  });

  const url = `https://maps.googleapis.com/maps/api/geocode/json?${params}`;
  console.log('[GeocodingCache] Making API call to:', url.replace(API_KEY, 'API_KEY'));

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Geocoding API HTTP error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[GeocodingCache] API response:', data);

  if (data.status === 'ZERO_RESULTS') {
    throw new Error(`No results found for: ${query}. Try a more specific location like "Sydney NSW" or "2000 Sydney NSW"`);
  }

  if (data.status === 'REQUEST_DENIED') {
    throw new Error(`API request denied: ${data.error_message || 'Check API key and restrictions'}`);
  }

  if (data.status === 'INVALID_REQUEST') {
    throw new Error(`Invalid request: ${data.error_message || 'Check query format'}`);
  }

  if (data.status === 'OVER_QUERY_LIMIT') {
    throw new Error(`Query limit exceeded: ${data.error_message || 'API quota reached'}`);
  }

  if (data.status !== 'OK' || !data.results || data.results.length === 0) {
    throw new Error(`Geocoding failed: Status=${data.status}, Error=${data.error_message || 'Unknown error'}`);
  }  const result = data.results[0];
  
  // Extract components
  const components = {};
  result.address_components.forEach(component => {
    component.types.forEach(type => {
      components[type] = component.long_name;
    });
  });
  
  // Prefer locality, fall back to postal_town or sublocality_level_1
  const locality = components.locality || 
                  components.postal_town || 
                  components.sublocality_level_1 || 
                  '';
  
  return {
    formattedAddress: result.formatted_address,
    location: result.geometry?.location ? {
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng
    } : null,
    components: {
      postal_code: components.postal_code || '',
      locality: locality,
      administrative_area_level_1: components.administrative_area_level_1 || '',
      country: components.country || 'AU'
    }
  };
}

// =============================================================================
// EXPORTS AND EXAMPLE USAGE
// =============================================================================

// Export the public API
const GeocodingCache = {
  // Main API
  geocodeWithCache,
  lookupGeocodeCache,
  storeGeocodeResult,
  
  // Utilities
  normalizeQuery,
  createReverseGeocodeKey,
  
  // Example implementation
  googleGeocodeAU,
  
  // Monitoring and Statistics
  getStats,
  resetStats,
  generateReport,
  trackStats,
  
  // Constants
  GEOCODING_CACHE_KEY,
  GEOCODING_COOKIE_KEY,
  GEOCODING_COOKIE_MAX_ENTRIES,
  GEOCODING_STATS_KEY
};

// Example usage:
/*
// Basic usage
try {
  const result = await GeocodingCache.geocodeWithCache(
    '2000', 
    GeocodingCache.googleGeocodeAU
  );
  
  console.log('Geocoding result:', result);
  console.log('Was cached:', result.fromCache);
  console.log('From cookie:', result.fromCookie);
} catch (error) {
  console.error('Geocoding failed:', error);
}

// Force refresh example
const freshResult = await GeocodingCache.geocodeWithCache(
  'Sydney NSW',
  GeocodingCache.googleGeocodeAU,
  { forceRefresh: true }
);

// Reverse geocoding example
const reverseKey = GeocodingCache.createReverseGeocodeKey(-33.8688, 151.2093);
const cachedReverse = GeocodingCache.lookupGeocodeCache(reverseKey);

// Direct cache lookup
const cached = GeocodingCache.lookupGeocodeCache('melbourne vic');
if (cached) {
  console.log('Found in cache:', cached);
} else {
  console.log('Not in cache, need to geocode');
}

// Clear cache to test new cookie format
console.log('[GeocodingCache] Clearing old cache format for consistency fix');
try {
  sessionStorage.removeItem('mjbale_geocoding_cache_v1');
  document.cookie = 'mjbale_geocoding_ck_v1=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
} catch (e) {
  // Ignore errors
}
*/

// For Node.js environments or module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GeocodingCache;
}

// For browser environments
if (typeof window !== 'undefined') {
  window.GeocodingCache = GeocodingCache;
}

export default GeocodingCache;
